import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import HomePage from "@/pages/HomePage";
import ZonesPage from "@/pages/ZonesPage";
import VisitTracker from "@/components/VisitTracker";
import { useSiteSettings, isPageVisible } from "@/lib/siteSettings";
import "@/App.css";

// Lazy-load większych podstron – pobierane dopiero po wejściu w sekcję
const SchedulePage = lazy(() => import("@/pages/SchedulePage"));
const ExhibitorsPage = lazy(() => import("@/pages/ExhibitorsPage"));
const InfoPage = lazy(() => import("@/pages/InfoPage"));
const DirectionsPage = lazy(() => import("@/pages/DirectionsPage"));
const GamesPage = lazy(() => import("@/pages/GamesPage"));
const PrototypesPage = lazy(() => import("@/pages/PrototypesPage"));
const WargamesPage = lazy(() => import("@/pages/WargamesPage"));
const BigFormatGamesPage = lazy(() => import("@/pages/BigFormatGamesPage"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));

function PageFallback() {
  return (
    <div className="flex items-center justify-center w-full h-screen" style={{ backgroundColor: "#F0F6FF" }}>
      <div style={{ color: "#5A7A9A", fontSize: 14 }}>Ładowanie...</div>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div className="flex w-full" style={{ backgroundColor: "#001a3a", minHeight: "100vh" }}>
      <Sidebar />
      <main
        className="flex-1 overflow-y-auto overflow-x-hidden min-w-0"
        style={{ height: "100vh" }}
        data-testid="main-content"
      >
        {children}
      </main>
    </div>
  );
}

/** Wrapper: pokazuje stronę tylko gdy `pages.<key>` != false w site_settings.json.
 *  W przeciwnym razie kieruje na `/`. Fail-open — brak flagi = strona widoczna. */
function ProtectedPage({ pageKey, children }) {
  const settings = useSiteSettings();
  // Dopóki settings nie załadowane (undefined) — renderujemy stronę żeby uniknąć błysku;
  // po odczycie ewentualnie redirect na home.
  if (settings && !isPageVisible(settings, pageKey)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <VisitTracker />
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/strefy" element={<ProtectedPage pageKey="zones"><ZonesPage /></ProtectedPage>} />
            <Route path="/wydarzenia" element={<ProtectedPage pageKey="schedule"><SchedulePage /></ProtectedPage>} />
            <Route path="/wystawcy" element={<ProtectedPage pageKey="exhibitors"><ExhibitorsPage /></ProtectedPage>} />
            <Route path="/atrakcje" element={<ProtectedPage pageKey="info"><InfoPage /></ProtectedPage>} />
            <Route path="/jak-dojade" element={<ProtectedPage pageKey="directions"><DirectionsPage /></ProtectedPage>} />
            <Route path="/katalog-gier" element={<ProtectedPage pageKey="schedule"><GamesPage /></ProtectedPage>} />
            <Route path="/prototypy" element={<ProtectedPage pageKey="schedule"><PrototypesPage /></ProtectedPage>} />
            <Route path="/gry-bitewne" element={<ProtectedPage pageKey="schedule"><WargamesPage /></ProtectedPage>} />
            <Route path="/gry-wieloosobowe" element={<ProtectedPage pageKey="schedule"><BigFormatGamesPage /></ProtectedPage>} />
            <Route path="/mapa" element={<ProtectedPage pageKey="map"><MapPage /></ProtectedPage>} />
            <Route path="/mgmt-lyp-4kwdw1jssrfrj0ss" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}
